import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createAdminGroup } from '../api';
import SearchField from './SearchField';
import Button from './ui/Button';

const initialFormState = {
  name: '',
  departmentId: '',
  rightIds: [],
};

/**
 * Creating a group: a name, the department it belongs to, and what it grants.
 *
 * All three at once on purpose. A group without a department cannot exist, and
 * one without rights grants nothing — leaving either for later would mean
 * creating something that does not yet mean anything.
 */
function AdminCreateGroupForm({ departments = [], rights = [], onCreated }) {
  const { t } = useTranslation();

  const [form, setForm] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rightsQuery, setRightsQuery] = useState('');

  // The filter narrows what is shown, never what is chosen: a right ticked and
  // then filtered out is still granted, which is why the count below the field
  // reports the whole selection rather than the visible part of it.
  const visibleRights = useMemo(() => {
    const needle = rightsQuery.trim().toLowerCase();

    if (needle === '') {
      return rights;
    }

    // By name only: matching the description too would pull in every right
    // whose explanation merely mentions the word typed.
    return rights.filter((right) =>
      right.right_name.toLowerCase().includes(needle),
    );
  }, [rights, rightsQuery]);

  function updateField(field) {
    return (event) => {
      setForm((current) => ({ ...current, [field]: event.target.value }));
      setError('');
      setSuccess('');
    };
  }

  /**
   * Group names are unique platform-wide, so the same role in two departments
   * has to be told apart by its name — the convention the migration used, and
   * the one every derived group already follows: "Author (Accounting)".
   *
   * The suffix is written into the field rather than added on submit: the name
   * is what will exist, and a form that quietly saves something other than what
   * it shows is worse than one that types for you. Switching departments
   * replaces the suffix instead of stacking a second one.
   */
  function chooseDepartment(event) {
    const departmentId = event.target.value;

    const department = departments.find(
      (candidate) => String(candidate.departmentId) === departmentId,
    );

    setForm((current) => {
      const withoutSuffix = current.name.replace(/\s*\([^()]*\)\s*$/, '');

      const suffix =
        department && !department.isPlatform ? ` (${department.name})` : '';

      return {
        ...current,
        departmentId,
        name: withoutSuffix ? `${withoutSuffix}${suffix}` : current.name,
      };
    });

    setError('');
    setSuccess('');
  }

  function toggleRight(rightId) {
    setForm((current) => ({
      ...current,
      rightIds: current.rightIds.includes(rightId)
        ? current.rightIds.filter((id) => id !== rightId)
        : [...current.rightIds, rightId],
    }));

    setError('');
    setSuccess('');
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const name = form.name.trim();

    if (name.length < 2) {
      setError(t('adminPanel.errorGroupName'));
      return;
    }

    if (form.departmentId === '') {
      setError(t('adminPanel.errorGroupDepartment'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      setSuccess('');

      const created = await createAdminGroup({
        name,
        departmentId: Number(form.departmentId),
        rightIds: form.rightIds,
      });

      setForm(initialFormState);
      setSuccess(t('adminPanel.groupCreated', { name: created.groupName }));

      if (onCreated) {
        await onCreated();
      }
    } catch (submitError) {
      console.error('Failed to create group:', submitError);
      setError(submitError?.message || t('adminPanel.errorGeneric'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="account-form" onSubmit={handleSubmit} noValidate>
      {error && (
        <div className="login-message login-message-error" role="alert">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="login-message login-message-success" role="status">
          <i className="fa-solid fa-circle-check" />
          <span>{success}</span>
        </div>
      )}

      <div className="account-field-row">
        <label htmlFor="adminNewGroupName">
          {t('adminPanel.groupNameLabel')}
        </label>

        <input
          id="adminNewGroupName"
          name="name"
          type="text"
          value={form.name}
          disabled={isSubmitting}
          onChange={updateField('name')}
        />
      </div>

      <div className="account-field-row">
        <label htmlFor="adminNewGroupDepartment">
          {t('adminPanel.departmentLabel')}
        </label>

        <select
          id="adminNewGroupDepartment"
          name="departmentId"
          value={form.departmentId}
          disabled={isSubmitting}
          onChange={chooseDepartment}
        >
          <option value="">{t('adminPanel.chooseDepartment')}</option>

          {departments.map((department) => (
            <option
              key={department.departmentId}
              value={department.departmentId}
            >
              {department.isPlatform
                ? `${department.name} — ${t('adminPanel.platformScope')}`
                : department.name}
            </option>
          ))}
        </select>
      </div>

      <div className="account-field-row">
        <label>{t('adminPanel.rightsLabel')}</label>

        <div>
          {/* One character is enough here: there are fourteen rights and the
              list is the point of the field, so it starts fully visible. */}
          <SearchField
            value={rightsQuery}
            onChange={setRightsQuery}
            placeholder={t('adminPanel.rightsSearchPlaceholder')}
            ariaLabel={t('adminPanel.rightsSearchAriaLabel')}
            minLength={1}
          />

          <p className="admin-search-hint">
            {t('adminPanel.rightsChosen', { count: form.rightIds.length })}
          </p>

        <div className="admin-group-options">
          {visibleRights.map((right) => (
            <label
              key={right.right_id}
              className="admin-group-option"
              title={right.description || ''}
            >
              <input
                type="checkbox"
                checked={form.rightIds.includes(right.right_id)}
                disabled={isSubmitting}
                onChange={() => toggleRight(right.right_id)}
              />

              <span>{right.right_name}</span>
            </label>
          ))}

          {visibleRights.length === 0 && (
            <p className="admin-user-list-empty">
              {t('adminPanel.noRightsFound')}
            </p>
          )}
        </div>
        </div>
      </div>

      <div className="account-actions">
        <Button
          variant="primary"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? t('adminPanel.creating')
            : t('adminPanel.createGroupSubmit')}
        </Button>
      </div>
    </form>
  );
}

export default AdminCreateGroupForm;
